import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useEditor } from "./useEditorState";

// Each control lists exactly what the runtime colours (runtime.js partSel): hover/focus/tap highlights it in the preview.
const CONTROLS = [
  ["background", "Sfondo", "bg", "Lo sfondo dell'intera sezione (dietro a testi, immagini e riquadri)."],
  ["text_color", "Colore testo", "text", "Tutti i testi della sezione (sopratitolo, titolo, sottotitolo, paragrafi, elenchi) tranne quelli dentro riquadri e pulsanti."],
  ["box_bg", "Sfondo riquadro", "box", "Il riquadro interno colorato della sezione (banner, card del modulo)."],
  ["box_text", "Testo riquadro", "box", "I testi dentro il riquadro interno."],
  ["button_bg", "Sfondo pulsante", "button", "I pulsanti e i link a pulsante della sezione (principale e secondario)."],
  ["button_text", "Testo pulsante", "button", "Il testo dei pulsanti della sezione."],
];

function lum(hex) {
  const v = hex.replace("#", "");
  const n = v.length === 3 ? v.split("").map((c) => c + c).join("") : v.slice(0, 6);
  const [r, g, b] = [0, 2, 4].map((i) => { const c = parseInt(n.slice(i, i + 2), 16) / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
const HEX = /^#[0-9a-fA-F]{6}$/;
function Contrast({ a, b, what, testid }) {
  if (!HEX.test(a || "") || !HEX.test(b || "")) return null;
  const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m);
  const ratio = (x + 0.05) / (y + 0.05);
  return ratio < 4.5 ? <p className="text-[11px] text-amber-700" role="status" data-testid={testid}>Contrasto basso {what} ({ratio.toFixed(1)}:1, consigliato almeno 4,5:1): potrebbe non leggersi bene. Puoi comunque salvare.</p> : null;
}

function useParts(id) {
  const ed = useEditor();
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    const on = (e) => { if (e.detail.id === id) setCounts(e.detail.counts); };
    window.addEventListener("adm:parts", on);
    return () => window.removeEventListener("adm:parts", on);
  }, [id]);
  useEffect(() => { const t = setTimeout(() => ed.frameRef.current?.send({ type: "probe", id }), 500); return () => clearTimeout(t); }, [id, ed.content]); // eslint-disable-line react-hooks/exhaustive-deps
  return counts;
}

export default function SectionColors({ s, st, setSt }) {
  const ed = useEditor();
  const counts = useParts(s.id);
  const timer = useRef(null);
  const show = (part) => { clearTimeout(timer.current); ed.frameRef.current?.send({ type: "highlight", id: s.id, part }); };
  const hide = () => { clearTimeout(timer.current); ed.frameRef.current?.send({ type: "highlight", id: null }); };
  const tap = (part) => { show(part); timer.current = setTimeout(hide, 2500); };
  useEffect(() => hide, [s.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-2" data-testid="section-colors">
      <p className="text-[11px] text-slate-500">Passa sopra o tocca un colore: nell'anteprima si evidenzia cosa cambia.</p>
      {CONTROLS.map(([k, label, part, hint]) => {
        const missing = part !== "bg" && counts && !counts[part];
        return (
          <div key={k} className="flex items-center justify-between gap-3" onMouseEnter={() => show(part)} onMouseLeave={hide} onFocus={() => show(part)} onBlur={hide} onPointerDown={(e) => e.pointerType === "touch" && tap(part)} data-testid={`style-row-${k}`}>
            <span className="flex min-w-0 items-center gap-1 text-xs text-slate-700">{label}
              <Tooltip><TooltipTrigger asChild><button type="button" className="text-slate-400 hover:text-slate-700" aria-label={`Cosa cambia: ${label}`} data-testid={`style-hint-${k}`}><Info size={12} /></button></TooltipTrigger>
                <TooltipContent side="top" align="start" className="max-w-[240px] text-xs">{hint}</TooltipContent></Tooltip>
            </span>
            {missing && !st[k]
              ? <span className="text-[11px] text-slate-400" data-testid={`style-missing-${k}`}>Non presente in questa sezione</span>
              : <span className="flex items-center gap-1.5">
                  <input type="color" value={st[k] || "#ffffff"} onChange={(e) => setSt({ [k]: e.target.value })} aria-label={label} aria-describedby={`hint-${k}`} className="h-7 w-8 rounded border p-0.5" data-testid={`style-${k.replace(/_/g, "-")}`} />
                  <span id={`hint-${k}`} className="sr-only">{hint}</span>
                  {st[k] && <button type="button" className="text-[11px] text-slate-500 hover:text-slate-900" onClick={() => setSt({ [k]: "" })} data-testid={`style-reset-${k}`}>reset</button>}
                  {missing && <span className="text-[10px] text-slate-400" title="Il colore è salvato ma questa sezione non ha l'elemento: non ha effetto" data-testid={`style-missing-${k}`}>non presente</span>}
                </span>}
          </div>);
      })}
      <Contrast a={st.background} b={st.text_color} what="tra testo e sfondo" testid="style-contrast-warning-text" />
      {counts?.button !== 0 && <Contrast a={st.button_bg} b={st.button_text} what="nel pulsante" testid="style-contrast-warning-button" />}
      {counts?.box !== 0 && <Contrast a={st.box_bg} b={st.box_text} what="nel riquadro" testid="style-contrast-warning-box" />}
    </div>);
}
